// frontend/src/components/help/VendorGuide.jsx - WITH PROPER BACK NAVIGATION
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, DollarSign, Users, Package, Star } from 'lucide-react';

const VendorGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we came from vendor dashboard settings
  const handleBack = () => {
    const userType = localStorage.getItem('userType');
    if (userType === 'vendor') {
      // Go back to vendor dashboard settings tab
      navigate('/vendor/dashboard', { 
        state: { activeTab: 'settings' } 
      });
    } else {
      // Go back to previous page
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <button 
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Settings
            </button>
            <div>
              <h1 className="text-2xl font-bold">Vendor Guide</h1>
              <p className="text-sm text-gray-600">Everything you need to succeed on UniLedge</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Getting Started */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Getting Started</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircle className="text-green-600 mr-3 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium">Complete Your Profile</h3>
                <p className="text-sm text-gray-600">Add business information, contact details, and verification documents.</p>
              </div>
            </div>
            <div className="flex items-start">
              <Package className="text-blue-600 mr-3 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium">Add Your Products</h3>
                <p className="text-sm text-gray-600">List products with bulk pricing to attract community groups.</p>
              </div>
            </div>
            <div className="flex items-start">
              <Users className="text-purple-600 mr-3 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium">Discover Groups</h3>
                <p className="text-sm text-gray-600">Find active groups that match your products and create targeted offers.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Best Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-3">Product Listings</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Use clear, descriptive product names</li>
                <li>• Include high-quality photos</li>
                <li>• Set competitive bulk pricing (25-40% savings)</li>
                <li>• Keep inventory status updated</li>
                <li>• Respond to member questions quickly</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-3">Group Communication</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Message group admins personally</li>
                <li>• Propose win-win partnerships</li>
                <li>• Be transparent about pricing</li>
                <li>• Offer flexible delivery options</li>
                <li>• Build long-term relationships</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">How Payments Work</h2>
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium text-center">
              You receive 85% of every order • Platform: 7.5% • Logistics: 7.5%
            </p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <span>Order Value: $1,000</span>
              <span className="font-medium">100%</span>
            </div>
            <div className="flex items-center justify-between py-2 text-red-600">
              <span>Platform Fee (7.5%)</span>
              <span>-$75</span>
            </div>
            <div className="flex items-center justify-between py-2 text-orange-600">
              <span>Logistics Fee (7.5%)</span>
              <span>-$75</span>
            </div>
            <div className="flex items-center justify-between py-2 border-t font-semibold text-green-600">
              <span>You Receive (85%)</span>
              <span>$850</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Payments are processed weekly on Fridays via direct bank transfer or check.
          </p>
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Common Questions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium">How do I find the right groups?</h3>
              <p className="text-sm text-gray-600 mt-1">
                Use our Group Discovery tool to find groups that match your products. Look for groups with high member counts and urgent deadlines.
              </p>
            </div>
            <div>
              <h3 className="font-medium">What makes a good bulk price?</h3>
              <p className="text-sm text-gray-600 mt-1">
                Groups expect 25-40% savings compared to retail. Price competitively while maintaining your margins.
              </p>
            </div>
            <div>
              <h3 className="font-medium">How do I handle large orders?</h3>
              <p className="text-sm text-gray-600 mt-1">
                Post logistics jobs to find community members who can help with delivery, setup, or coordination.
              </p>
            </div>
            <div>
              <h3 className="font-medium">When do I get paid?</h3>
              <p className="text-sm text-gray-600 mt-1">
                Payments are processed weekly on Fridays, 3 days after order completion. You'll receive 85% of the order value.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorGuide;